import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo5Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 5 BWV 776
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV776/bach-invention-05/bach-invention-05-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 5 BWV 776.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-4833">
      <Navbar data-id="element-4834" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-4835">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-4836">
          <img src="https://images.unsplash.com/photo-1510915361894-db8b60106cb1?w=400&h=300&fit=crop" alt="Invention No. 5" className="w-full h-64 md:h-80 object-cover" data-id="element-4837" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-4838" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-4839">
            <div className="max-w-7xl mx-auto" data-id="element-4840">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-4841">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-4842" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-4843">
                Invention No. 5 in E-flat major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-4844">BWV 776</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-4845">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-4846">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-4847">
          <div className="flex items-center space-x-2" data-id="element-4848">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-4849">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-4850" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4851">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-4852">
                  <ZoomInIcon className="h-5 w-5" data-id="element-4853" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-4854">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-4855">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-4856" />
              <span className="hidden sm:inline" data-id="element-4857">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-4858">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-4859" />
              <span className="hidden sm:inline" data-id="element-4860">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-4861">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-4862">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-4863">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 5 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-4864" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-4865">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4866">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-4867">
            <div data-id="element-4868">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4869">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-4870">
                <div data-id="element-4871">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4872">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4873">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-4874">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4875">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4876">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-4877">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4878">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4879">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-4880">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4881">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4882">
                    E-flat major
                  </dd>
                </div>
                <div data-id="element-4883">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-4884">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4885">
                    BWV 776
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-4886">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-4887">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-4888">
                The fifth of Bach's fifteen Two-Part Inventions, this piece in
                E-flat major stands out for its warm, lyrical character and
                sophisticated harmonic language. Written as pedagogical works
                for keyboard students, the Inventions teach the art of two-voice
                counterpoint while developing musical ideas through imitation
                and elaboration. This invention features a flowing, song-like
                subject that moves in gentle arcs, creating an expressive and
                intimate atmosphere. The use of E-flat major, with its three
                flats, gives the piece a rich, mellow sonority that
                distinguishes it from the brighter major-key inventions. The
                continuous interplay between the two voices demonstrates Bach's
                mastery of invertible counterpoint, where melodic material is
                shared equally between both hands. The work requires careful
                attention to voicing and legato touch to bring out its singing
                quality, while maintaining the independence of each line. This
                invention serves as both an essential study in baroque keyboard
                technique and a beautiful miniature that showcases Bach's
                ability to create profound musical expression within a concise,
                pedagogical framework.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-4889" />
    </div>;
};